import { Zap, Brain, Crown, Sparkles, Lock, ChevronDown, Check, Sun, Globe, Moon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// ChatGPT-style model picker — single button showing the active model,
// tap to reveal a dropdown of every available model. Locked models still
// render so the tap fires the upgrade flow (main conversion lever).
//
// Selected model is persisted by the parent Chat page via localStorage —
// see `smartai_chat_model` there.

const MODELS = [
  {
    id: "gemini-3-flash-preview",
    key: "quick",
    name: "Gemini 3 Flash",
    tagline: "Fast · Free",
    Icon: Zap,
    tint: "text-emerald-600",
    tier: "free",
  },
  {
    id: "claude-sonnet-4-6",
    key: "smart",
    name: "Claude Sonnet 4.6",
    tagline: "Balanced · Deeper reasoning",
    Icon: Brain,
    tint: "text-zinc-700",
    tier: "creator",
  },
  {
    id: "gpt-5",
    key: "genius",
    name: "GPT-5",
    tagline: "Top-tier reasoning",
    Icon: Crown,
    tint: "text-amber-600",
    tier: "pro",
  },
  {
    id: "claude-opus-4-5-20251101",
    key: "master",
    name: "Claude Opus 4.5",
    tagline: "Most capable · long-form",
    Icon: Sparkles,
    tint: "text-fuchsia-600",
    tier: "pro",
  },
  // GPT 5.6 family — Pro/Power exclusive. Three variants so the user
  // picks between depth (Sol), balance (Terra), and speed (Luna).
  {
    id: "gpt-5.6-sol",
    key: "sol",
    name: "GPT 5.6 Sol",
    tagline: "Deepest 5.6 · long analysis",
    Icon: Sun,
    tint: "text-orange-500",
    tier: "pro",
  },
  {
    id: "gpt-5.6-terra",
    key: "terra",
    name: "GPT 5.6 Terra",
    tagline: "Balanced 5.6 · everyday power",
    Icon: Globe,
    tint: "text-teal-500",
    tier: "pro",
  },
  {
    id: "gpt-5.6-luna",
    key: "luna",
    name: "GPT 5.6 Luna",
    tagline: "Fastest 5.6 · snappy chat",
    Icon: Moon,
    tint: "text-blue-500",
    tier: "pro",
  },
];

const TIER_RANK = { free: 0, creator: 1, pro: 2, power: 2, admin: 3 };
const meets = (planId, required) =>
  (TIER_RANK[planId] ?? 0) >= (TIER_RANK[required] ?? 0);

export default function ChatModelDropdown({
  model,
  onChange,
  userPlan = "free",
  flagState = {},
  className = "",
}) {
  // Filter out models the admin has fully hidden.
  const visible = MODELS.filter((m) => flagState[m.key] !== "hidden");
  const current = visible.find((m) => m.id === model) || visible[0];
  const CurrentIcon = current?.Icon || Zap;

  const handlePick = (m) => {
    const state = flagState[m.key] || "enabled";
    if (state === "coming_soon") return;
    const locked = !meets(userPlan, m.tier);
    onChange(m.id, { locked, mode: { ...m, label: m.name } });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="chat-model-dropdown-trigger"
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50 active:bg-zinc-100 transition-colors ${className}`}
          title={current?.tagline}
        >
          <CurrentIcon className={`w-4 h-4 ${current?.tint || "text-zinc-600"} shrink-0`} />
          <span className="text-sm font-bold text-zinc-900 truncate max-w-[160px] sm:max-w-none">
            {current?.name || "Model"}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-72 p-1.5"
        data-testid="chat-model-dropdown-menu"
      >
        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 px-2 py-1.5">
          Choose a model
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {visible.map((m) => {
          const state = flagState[m.key] || "enabled";
          const locked = !meets(userPlan, m.tier);
          const isComingSoon = state === "coming_soon";
          const isNew = state === "new";
          const isActive = current?.id === m.id;
          const Icon = m.Icon;

          return (
            <DropdownMenuItem
              key={m.id}
              disabled={isComingSoon}
              onSelect={(e) => {
                // Radix closes on select — that's the desired behavior.
                // For locked items we still fire onChange so the upgrade
                // modal appears; parent handles blocking the model switch.
                e.preventDefault();
                handlePick(m);
                // Manually close by dispatching Escape — easiest cross-radix
                document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
              }}
              data-testid={`chat-model-option-${m.key}`}
              className={`flex items-start gap-2.5 px-2.5 py-2.5 rounded-md cursor-pointer ${
                isActive ? "bg-zinc-50" : ""
              }`}
            >
              <Icon className={`w-4 h-4 mt-0.5 ${m.tint} shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-zinc-900 truncate">{m.name}</span>
                  {isNew && !isComingSoon && (
                    <span className="text-[8.5px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500 text-white">
                      New
                    </span>
                  )}
                  {isComingSoon && (
                    <span className="text-[8.5px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-zinc-200 text-zinc-600">
                      Soon
                    </span>
                  )}
                  {locked && !isComingSoon && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200">
                      <Lock className="w-2.5 h-2.5" />
                      {m.tier === "pro" ? "Pro" : "Creator"}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{m.tagline}</div>
              </div>
              {isActive && <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-1" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { MODELS as CHAT_MODELS };
